import React, { useState, useRef, useEffect } from 'react';
import {
  Modal,
  Button,
  SpaceBetween,
  Box,
  Input,
  Container,
  Header,
  Alert,
  Spinner
} from '@cloudscape-design/components';
import { titleAPI } from '../services/api';

const ChatBot = ({ visible, onDismiss }) => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'bot',
      content: '👋 Hi! I\'m your Title Manager assistant. I can help you search, create, and manage content. Try asking me something like "search for action movies" or "list all content".',
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => 
    'session-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9)
  );
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const processMessage = async (userMessage) => {
    try {
      // Use AI Assistant Lambda with session ID
      const response = await titleAPI.chatWithAI(userMessage, sessionId);
      
      // Update session ID if reset
      if (response.data.session_id && response.data.session_id !== sessionId) {
        setSessionId(response.data.session_id);
      }
      
      return response.data.response;

      
    } catch (error) {
      console.error('Chat error:', error);
      
      // Handle specific error types
      if (error.code === 'ERR_NETWORK') {
        return `🔐 **Authentication Required:** Please make sure you're logged in and try again. The AI Assistant requires valid authentication.`;
      }
      
      if (error.response?.status === 404) {
        return `🚧 **AI Assistant Unavailable:** The AI chat feature is currently being deployed. Please try the regular search and content management features instead.`;
      }
      
      if (error.response?.status === 401) {
        return `🔐 **Authentication Failed:** Your session may have expired. Please refresh the page and log in again.`;
      }
      
      return `❌ **Error:** ${error.response?.data?.error || error.message || 'Something went wrong'}`;
    }
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || loading) return;

    const userMessage = inputValue.trim();
    const newUserMessage = {
      id: Date.now(),
      type: 'user',
      content: userMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, newUserMessage]);
    setInputValue('');
    setLoading(true);

    try {
      const response = await titleAPI.chatWithAI(userMessage);
      const botResponse = response.data.response;
      
      const newBotMessage = {
        id: Date.now() + 1,
        type: 'bot',
        content: botResponse,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, newBotMessage]);
      
      // Handle reset command
      if (response.data.reset) {
        setTimeout(() => {
          setMessages([{
            id: 1,
            type: 'bot',
            content: '👋 Hi! I\'m your Title Manager assistant. I can help you search, create, and manage content.',
            timestamp: new Date()
          }]);
        }, 1000);
      }
    } catch (error) {
      const errorMessage = {
        id: Date.now() + 1,
        type: 'bot',
        content: `❌ Sorry, I encountered an error: ${error.message}`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatMessage = (content) => {
    // Simple markdown-like formatting
    return content
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\n/g, '<br/>')
      .replace(/• /g, '• ');
  };

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <span>Title Manager Assistant</span>
          <Button 
            variant="normal" 
            iconName="refresh" 
            onClick={() => {
              const newSessionId = 'session-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
              setSessionId(newSessionId);
              setMessages([{
                id: 1,
                type: 'bot',
                content: '🔄 **Chat Reset**: Starting a new conversation. How can I help you?',
                timestamp: new Date()
              }]);
            }}
          >
            New Chat
          </Button>
        </div>
      }
      size="large"
      footer={
        <Box float="right">
          <Button variant="link" onClick={onDismiss}>
            Close
          </Button>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <Container>
          <div style={{ 
            height: '400px', 
            overflowY: 'auto', 
            padding: '16px',
            border: '1px solid #e9ebed',
            borderRadius: '8px',
            backgroundColor: '#fafbfc'
          }}>
            {messages.map((message) => (
              <div
                key={message.id}
                style={{
                  marginBottom: '16px',
                  display: 'flex',
                  justifyContent: message.type === 'user' ? 'flex-end' : 'flex-start'
                }}
              >
                <div
                  style={{
                    maxWidth: '80%',
                    padding: '12px 16px',
                    borderRadius: '12px',
                    backgroundColor: message.type === 'user' ? '#0073bb' : '#ffffff',
                    color: message.type === 'user' ? 'white' : '#16191f',
                    border: message.type === 'bot' ? '1px solid #e9ebed' : 'none',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                  }}
                >
                  <div 
                    dangerouslySetInnerHTML={{ 
                      __html: formatMessage(message.content) 
                    }}
                    style={{
                      fontSize: '14px',
                      lineHeight: '1.5'
                    }}
                  />
                  <div
                    style={{
                      fontSize: '11px',
                      opacity: 0.7,
                      marginTop: '4px'
                    }}
                  >
                    {message.timestamp.toLocaleTimeString()}
                  </div>
                </div>
              </div>
            ))}
            
            {loading && (
              <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '16px' }}>
                <div style={{
                  padding: '12px 16px',
                  borderRadius: '12px',
                  backgroundColor: '#ffffff',
                  border: '1px solid #e9ebed',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <Spinner size="small" />
                  <span style={{ fontSize: '14px', color: '#5f6b7a' }}>Thinking...</span>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
        </Container>

        <div style={{ display: 'flex', gap: '8px' }}>
          <div style={{ flex: 1 }}>
            <Input
              value={inputValue}
              onChange={({ detail }) => setInputValue(detail.value)}
              onKeyDown={handleKeyPress}
              placeholder="Ask me about titles, search content, or create new items..."
              disabled={loading}
            />
          </div>
          <Button
            variant="primary"
            onClick={handleSendMessage}
            disabled={!inputValue.trim() || loading}
          >
            Send
          </Button>
        </div>

        <Box variant="small" color="text-body-secondary">
          💡 Try: "list all content", "search action movies", "create series Game of Thrones", or "help"
        </Box>
      </SpaceBetween>
    </Modal>
  );
};

export default ChatBot;